import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Form,
  Alert,
  Spinner
} from "reactstrap";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { GetStores, GetDeliverySettings, UpdateDeliverySettings } from "../../requests";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from "react-toastify";

const extractData = (response) => {
  if (!response) return null;
  if (response.data !== undefined) {
    if (response.data && response.data.success && response.data.data !== undefined) {
      return response.data.data;
    }
    return response.data;
  }
  if (response.success && response.data !== undefined) {
    return response.data;
  }
  return response;
};

const DeliverySettings = () => {
  document.title = "Delivery Settings | Susipizza";

  // A store-tied admin (role "admin") only manages their own store's
  // delivery configuration — mirrors the same lock pattern already used in
  // admin/src/Pages/StoreMenu/index.jsx. Previously every admin got the
  // free-choice dropdown, so any store-tied admin could switch to and view
  // (though not save, since PUT /delivery-settings/:storeId already
  // enforces checkStoreScope server-side) any other store's settings.
  const userRole = localStorage.getItem("role") || "admin";
  const userStoreId = localStorage.getItem("storeId") || "";
  const userStoreName = localStorage.getItem("storeName") || "";
  const isSuperAdmin = userRole === "superadmin";

  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(isSuperAdmin ? "" : userStoreId);
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      freeDeliveryAbove: "",
      maximumDeliveryRadius: 5,
      slabs: []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "slabs"
  });

  // Fetch store list on mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoadingStores(true);
        const resp = await GetStores();
        const storeList = extractData(resp) || [];
        setStores(storeList);
        if (isSuperAdmin && storeList.length > 0) {
          setSelectedStoreId(storeList[0].id || storeList[0]._id);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load stores.");
      } finally {
        setLoadingStores(false);
      }
    };
    fetchStores();
  }, []);

  // Fetch settings when store selection changes
  useEffect(() => {
    if (selectedStoreId) {
      fetchSettings(selectedStoreId);
    }
  }, [selectedStoreId]);

  const fetchSettings = async (storeId) => {
    try {
      setLoadingSettings(true);
      const token = localStorage.getItem("token");
      const resp = await GetDeliverySettings(token, storeId);
      const settings = extractData(resp) || {};

      const thresholdVal = settings.freeDeliveryAbove !== undefined ? settings.freeDeliveryAbove : settings.freeDeliveryThreshold;
      const slabsVal = settings.slabs || (settings.distancePricing || []).map(s => ({
        from: s.from !== undefined ? s.from : s.min,
        to: s.to !== undefined ? s.to : s.max,
        charge: s.charge
      }));

      reset({
        freeDeliveryAbove: thresholdVal !== null && thresholdVal !== undefined ? thresholdVal : "",
        maximumDeliveryRadius: settings.maximumDeliveryRadius !== null && settings.maximumDeliveryRadius !== undefined ? settings.maximumDeliveryRadius : "",
        slabs: slabsVal
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load delivery settings for this store.");
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleReset = () => {
    if (selectedStoreId) {
      fetchSettings(selectedStoreId);
      toast.info("Settings reset to current configuration.");
    }
  };

  const onError = (errors) => {
    console.error("Form validation errors:", errors);
    toast.error("Please fill in all pricing slabs and required fields correctly.");
  };

  const onSubmit = async (data) => {
    if (!selectedStoreId) {
      toast.error("Please select a store first.");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");

      const threshold = data.freeDeliveryAbove !== "" && data.freeDeliveryAbove !== null
        ? Number(data.freeDeliveryAbove)
        : null;
      const maxRadius = Number(data.maximumDeliveryRadius);
      const slabs = (data.slabs || []).map(s => ({
        from: Number(s.from),
        to: Number(s.to),
        charge: Number(s.charge)
      }));

      // Validation 1: from < to and positive values
      for (const slab of slabs) {
        if (slab.from < 0 || slab.to < 0 || slab.charge < 0) {
          toast.error("All distances and charges must be positive values.");
          setSaving(false);
          return;
        }
        if (slab.from >= slab.to) {
          toast.error(`Invalid range: Minimum distance (${slab.from} KM) must be less than Maximum distance (${slab.to} KM).`);
          setSaving(false);
          return;
        }
      }

      // Sort slabs to check overlaps
      const sorted = [...slabs].sort((a, b) => a.from - b.from);

      // Validation 2: No overlapping distance ranges
      for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i].to > sorted[i + 1].from) {
          toast.error(`Slab ranges overlap between ${sorted[i].from}-${sorted[i].to} KM and ${sorted[i + 1].from}-${sorted[i + 1].to} KM.`);
          setSaving(false);
          return;
        }
      }

      // Validation 3: Maximum radius cannot exceed the last pricing slab's maximum distance
      if (sorted.length > 0) {
        const lastSlab = sorted[sorted.length - 1];
        if (maxRadius > lastSlab.to) {
          toast.error(`Maximum delivery radius (${maxRadius} KM) cannot exceed the last pricing slab maximum distance (${lastSlab.to} KM).`);
          setSaving(false);
          return;
        }
      }

      const payload = {
        storeId: selectedStoreId,
        storeName: stores.find(s => (s.id || s._id) === selectedStoreId)?.name || "",
        freeDeliveryAbove: threshold,
        maximumDeliveryRadius: maxRadius,
        slabs: slabs
      };

      await UpdateDeliverySettings(token, selectedStoreId, payload);
      toast.success("Delivery settings saved successfully!");
      fetchSettings(selectedStoreId);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to update delivery settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Settings" breadcrumbItem="Delivery Settings" />

        {/* Global Saving Screen Overlay Spinner */}
        <LoadingSpinner loading={saving || loadingStores} />

        {/* Store Selector */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <CardBody>
                <FormGroup className="mb-0">
                  <Label for="storeSelect" className="fw-semibold">Configure Store Outlet</Label>
                  {!isSuperAdmin ? (
                    <div className="p-2 border rounded bg-light fw-semibold">
                      <i className="mdi mdi-store-outline me-2 text-primary" />
                      {userStoreName || "Your Scoped Store"}
                    </div>
                  ) : loadingStores ? (
                    <Spinner size="sm" color="primary" className="ms-2" />
                  ) : (
                    <Input
                      type="select"
                      id="storeSelect"
                      value={selectedStoreId}
                      onChange={(e) => setSelectedStoreId(e.target.value)}
                    >
                      <option value="">-- Select Store --</option>
                      {stores.map((s) => (
                        <option key={s.id || s._id} value={s.id || s._id}>
                          {s.name}{s.city ? ` (${s.city})` : ""}
                        </option>
                      ))}
                    </Input>
                  )}
                </FormGroup>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {selectedStoreId ? (
          <Form key={selectedStoreId} onSubmit={handleSubmit(onSubmit, onError)}>
            {loadingSettings ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading store delivery configuration...</p>
              </div>
            ) : (
              <>
                {/* General Settings */}
                <Row className="mb-4">
                  <Col md={12}>
                    <Card>
                      <CardBody>
                        <h5 className="card-title mb-4">General Settings</h5>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="freeDeliveryAbove" className="fw-semibold">Free Delivery Above Subtotal (₹)</Label>
                              <Controller
                                name="freeDeliveryAbove"
                                control={control}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id="freeDeliveryAbove"
                                    type="number"
                                    placeholder="e.g. 300"
                                    min="0"
                                    value={field.value !== undefined && field.value !== null ? field.value : ""}
                                  />
                                )}
                              />
                              <small className="text-muted">
                                Orders above this subtotal automatically receive free delivery. Leave empty to disable.
                              </small>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="maximumDeliveryRadius" className="fw-semibold">Maximum Delivery Radius (KM)</Label>
                              <Controller
                                name="maximumDeliveryRadius"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id="maximumDeliveryRadius"
                                    type="number"
                                    step="0.1"
                                    min="0.1"
                                    placeholder="e.g. 5"
                                    value={field.value !== undefined && field.value !== null ? field.value : ""}
                                  />
                                )}
                              />
                              <small className="text-muted">
                                Orders placed outside this radius cannot be delivered.
                              </small>
                            </FormGroup>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Distance Pricing Table */}
                <Row className="mb-4">
                  <Col md={12}>
                    <Card>
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <h5 className="card-title mb-0">Distance Based Delivery Charges</h5>
                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            onClick={() => append({ from: "", to: "", charge: "" })}
                          >
                            <i className="mdi mdi-plus me-1" /> Add Slab
                          </Button>
                        </div>

                        <Table responsive bordered hover className="align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Minimum Distance (KM)</th>
                              <th>Maximum Distance (KM)</th>
                              <th>Delivery Charge (₹)</th>
                              <th style={{ width: "100px" }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fields.map((field, index) => {
                              const { ref: fromRef, ...fromRest } = register(`slabs.${index}.from`, { required: true });
                              const { ref: toRef, ...toRest } = register(`slabs.${index}.to`, { required: true });
                              const { ref: chargeRef, ...chargeRest } = register(`slabs.${index}.charge`, { required: true });
                              return (
                              <tr key={field.id}>
                                <td>
                                  <Input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    placeholder="e.g. 0"
                                    required
                                    defaultValue={field.from}
                                    innerRef={fromRef}
                                    {...fromRest}
                                  />
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    step="0.1"
                                    min="0.1"
                                    placeholder="e.g. 1"
                                    required
                                    defaultValue={field.to}
                                    innerRef={toRef}
                                    {...toRest}
                                  />
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    min="0"
                                    placeholder="e.g. 20"
                                    required
                                    defaultValue={field.charge}
                                    innerRef={chargeRef}
                                    {...chargeRest}
                                  />
                                </td>
                                <td className="text-center">
                                  <Button
                                    type="button"
                                    color="danger"
                                    size="sm"
                                    onClick={() => remove(index)}
                                  >
                                    <i className="mdi mdi-delete" />
                                  </Button>
                                </td>
                              </tr>
                              );
                            })}
                            {fields.length === 0 && (
                              <tr>
                                <td colSpan="4" className="text-center text-muted py-4">
                                  No pricing slabs defined. Click "Add Slab" to construct pricing rules.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Advanced Configurations Placeholder (Future Scope) */}
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="border border-dashed">
                      <CardBody>
                        <h5 className="card-title text-muted mb-4">
                          <i className="mdi mdi-star-circle me-1" /> Premium Pricing Engines (Coming Soon)
                        </h5>
                        <Row className="text-muted">
                          <Col md={3}>
                            <FormGroup check inline disabled>
                              <Label check>
                                <Input type="checkbox" disabled /> Store-wise Exceptions
                              </Label>
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup check inline disabled>
                              <Label check>
                                <Input type="checkbox" disabled /> Holiday Surcharges
                              </Label>
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup check inline disabled>
                              <Label check>
                                <Input type="checkbox" disabled /> Rain Surge Pricing
                              </Label>
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup check inline disabled>
                              <Label check>
                                <Input type="checkbox" disabled /> Peak Hour Surcharges
                              </Label>
                            </FormGroup>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Form Action Buttons */}
                <Row>
                  <Col md={12} className="text-end">
                    <Button
                      type="button"
                      color="secondary"
                      className="me-2"
                      disabled={saving}
                      onClick={handleReset}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      color="success"
                      disabled={saving}
                    >
                      {saving ? "Saving Settings..." : "Save Settings"}
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </Form>
        ) : (
          <Alert color="warning">
            Please configure and select a Store Outlet from the dropdown to manage its delivery settings.
          </Alert>
        )}
      </Container>
    </div>
  );
};

export default DeliverySettings;
